import { DestroyRef, NgZone, inject } from '@angular/core';

/**
 * Document listener outside Angular: the handler enters Angular (change detection) only when the guard says
 * the event concerns this component. A @HostListener('document:...') runs a change detection of the whole app
 * on every key press or click, once per component instance.
 * Call it in an injection context (field initializer or constructor); removed when the component is destroyed.
 */
export function listenOutsideAngular<K extends keyof DocumentEventMap>(
  type: K,
  guard: (event: DocumentEventMap[K]) => boolean,
  handler: (event: DocumentEventMap[K]) => void
): void {
  const zone = inject(NgZone);
  const destroyRef = inject(DestroyRef);
  const listener = (event: DocumentEventMap[K]) => {
    if (guard(event)) {
      zone.run(() => handler(event));
    }
  };
  zone.runOutsideAngular(() => document.addEventListener(type, listener));
  destroyRef.onDestroy(() => document.removeEventListener(type, listener));
}

/** Same for window events (resize...): passive listener, enters Angular only when the guard returns true. */
export function listenWindowOutsideAngular<K extends keyof WindowEventMap>(
  type: K,
  guard: (event: WindowEventMap[K]) => boolean,
  handler: (event: WindowEventMap[K]) => void
): void {
  const zone = inject(NgZone);
  const destroyRef = inject(DestroyRef);
  const listener = (event: WindowEventMap[K]) => {
    if (guard(event)) {
      zone.run(() => handler(event));
    }
  };
  zone.runOutsideAngular(() => window.addEventListener(type, listener, { passive: true }));
  destroyRef.onDestroy(() => window.removeEventListener(type, listener));
}
